/**
 * FlahaSOIL v2 — Water-retention curve (dark-native tokens).
 */
import { Box, Stack, Typography, Tooltip } from "@mui/material";
import type { WaterRetentionAnalysisBlock } from "@flaha/shared-types";
import type { ScientificChartData } from "../types/ScientificChartTypes";
import { SpecialNote } from "../../../results/components/SpecialNote";
import { useChartTheme } from "../utils/useChartTheme";

const W = 480;
const H = 280;
const PAD_L = 44;
const PAD_R = 16;
const PAD_T = 16;
const PAD_B = 36;
const PF_MAX = 4.2;

function xAt(pF: number): number {
  const clamped = Math.max(0, Math.min(PF_MAX, pF));
  return PAD_L + (clamped / PF_MAX) * (W - PAD_L - PAD_R);
}
function yAt(theta: number, max: number): number {
  const t = Math.max(0, Math.min(max, theta));
  return H - PAD_B - (t / max) * (H - PAD_T - PAD_B);
}

interface WaterRetentionCurveChartProps {
  chartData: ScientificChartData<WaterRetentionAnalysisBlock | null>;
}

export function WaterRetentionCurveChart({ chartData }: WaterRetentionCurveChartProps) {
  const chartTheme = useChartTheme();
  const { data: retention, accessibilitySummary } = chartData;
  const print = chartTheme.label === "#241A14";
  const tickFont = print ? 13 : 11;
  const markFont = print ? 13 : 11;

  if (!retention) {
    return (
      <Typography variant="body2" color="text.secondary">
        No retention curve — sand & clay percentages required.
      </Typography>
    );
  }

  const thetaMax = Math.max(
    50,
    retention.saturation.waterContentVolPercent,
    ...retention.points.map((p) => p.waterContentVolPercent)
  );

  const path = retention.points
    .slice()
    .sort((a, b) => a.pF - b.pF)
    .map((p, i) => `${i === 0 ? "M" : "L"} ${xAt(p.pF).toFixed(2)} ${yAt(p.waterContentVolPercent, thetaMax).toFixed(2)}`)
    .join(" ");

  const anchors = [
    { key: "sat", pF: 0, theta: retention.saturation.waterContentVolPercent, color: chartTheme.saturation, label: "Sat", desc: "Saturation" },
    { key: "fc", pF: retention.fieldCapacity.pF, theta: retention.fieldCapacity.waterContentVolPercent, color: chartTheme.fieldCapacity, label: "FC", desc: "Field Capacity" },
    { key: "mad", pF: retention.irrigationThreshold.pF, theta: retention.irrigationThreshold.waterContentVolPercent, color: chartTheme.irrigation, label: "MAD", desc: "Irrigation Threshold" },
    { key: "wp", pF: retention.wiltingPoint.pF, theta: retention.wiltingPoint.waterContentVolPercent, color: chartTheme.wiltingPoint, label: "WP", desc: "Wilting Point" },
  ];

  return (
    <Stack spacing={1.5} aria-label={accessibilitySummary}>
      <Box
        component="svg"
        data-chart-core="waterRetention"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        sx={{
          width: "100%",
          height: "auto",
          maxWidth: W,
          borderRadius: 1,
          backgroundColor: chartTheme.plotBg,
          border: `1px solid ${chartTheme.plotBorder}`,
        }}
      >
        {[0, 1, 2, 3, 4].map((pF) => (
          <line
            key={`vg-${pF}`}
            x1={xAt(pF)}
            y1={PAD_T}
            x2={xAt(pF)}
            y2={H - PAD_B}
            stroke={chartTheme.grid}
          />
        ))}
        {[0, 10, 20, 30, 40, 50].filter((v) => v <= thetaMax).map((v) => (
          <line
            key={`hg-${v}`}
            x1={PAD_L}
            y1={yAt(v, thetaMax)}
            x2={W - PAD_R}
            y2={yAt(v, thetaMax)}
            stroke={chartTheme.grid}
          />
        ))}
        <line x1={PAD_L} y1={H - PAD_B} x2={W - PAD_R} y2={H - PAD_B} stroke={chartTheme.axisStrong} strokeWidth={1.25} />
        <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={H - PAD_B} stroke={chartTheme.axisStrong} strokeWidth={1.25} />
        {[0, 1, 2, 3, 4].map((pF) => (
          <g key={pF}>
            <line x1={xAt(pF)} y1={H - PAD_B} x2={xAt(pF)} y2={H - PAD_B + 4} stroke={chartTheme.axis} />
            <text x={xAt(pF)} y={H - PAD_B + 18} fontSize={tickFont} fontWeight={600} textAnchor="middle" fill={chartTheme.label}>
              pF {pF}
            </text>
          </g>
        ))}
        {[0, 10, 20, 30, 40, 50].filter((v) => v <= thetaMax).map((v) => (
          <g key={v}>
            <line x1={PAD_L - 4} y1={yAt(v, thetaMax)} x2={PAD_L} y2={yAt(v, thetaMax)} stroke={chartTheme.axis} />
            <text x={PAD_L - 6} y={yAt(v, thetaMax) + 3} fontSize={tickFont} fontWeight={600} textAnchor="end" fill={chartTheme.label}>
              {v}
            </text>
          </g>
        ))}
        <path
          d={path}
          fill="none"
          stroke={chartTheme.seriesPrimary}
          strokeWidth={print ? 2.75 : 2.25}
          data-testid="retention-curve"
        />
        {anchors.map((a) => (
          <Tooltip key={a.key} title={`${a.desc}: ${a.theta.toFixed(1)}% v/v at pF ${a.pF.toFixed(2)}`} arrow>
            <g style={{ cursor: "help" }}>
              <circle
                cx={xAt(a.pF)}
                cy={yAt(a.theta, thetaMax)}
                r={print ? 6 : 5}
                fill={a.color}
                stroke={chartTheme.pointStroke}
                strokeWidth={1.5}
              />
              <text x={xAt(a.pF) + 7} y={yAt(a.theta, thetaMax) - 7} fontSize={markFont} fill={a.color} fontWeight={700}>
                {a.label}
              </text>
            </g>
          </Tooltip>
        ))}
      </Box>
      <Box data-print-hide>
        <Typography variant="body2" color="text.secondary">
          {`PAW = ${retention.plantAvailableWater.toFixed(1)} ${retention.units.plantAvailableWater} · MAD ${(retention.madFraction * 100).toFixed(0)} % · ${retention.textureClass}`}
        </Typography>
        <SpecialNote
          kind="estimate"
          title="Model estimate — not a lab moisture curve"
          dense
          detail="Method: Saxton & Rawls (2006) pedotransfer functions from texture and organic matter."
        >
          This curve is estimated for planning. It is not a laboratory moisture-release measurement.
        </SpecialNote>
      </Box>
    </Stack>
  );
}
